import React, { useEffect, useRef, useState } from "react";
import { FontIcon, Stack, IDropdownOption } from "@fluentui/react";
import ProductItems from "./ProductItems";
import Receipts from "./Receipts";
import Corporations from "./Corporations";
import Brands from "./Brands";
import { useAppContext } from "./AppContext";
import { MdFilterAlt, MdClear, MdSearch } from "react-icons/md";
import ActionButton from "./ActionButton";
import SummaryTable from "./SummaryTable";
import { Cell, Legend, Pie, PieChart, PieLabelRenderProps, Sector } from 'recharts';
import { useNavigate } from "react-router";
import Categories from "./Categories";
import Merchant from "./Merchant";
import Merchants from "./Merchants";
import HeathMap from "./HeathMap";
import SpendingCharts from "./SpendingCharts";
import ProgressBar from "./ProgressBar";
import SConsciousApiService from "../services/SConsciousApiService";

type MonthOption = {
    id: number;
    key: string;
    text: string;
};

export default function Reports() {

    const {appData, setAppData} = useAppContext();
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
    const [originalReceipts, setOriginalReceipts] = useState<any[]>([]);
    const [originalProductItems, setOriginalProductItems] = useState<any[]>([]);
    const [originalBrands, setOriginalBrands] = useState<any[]>([]);
    const [originalCorporations, setOriginalCorporations] = useState<any[]>([]);
    const COLORS = ['#1a5f3c', '#2d7a4f', '#40956b', '#5bb088', '#7bc9a6', '#9ddcc4', '#c1ede1', '#1f6b44', '#2e8557', '#4da06f', '#6fba8c'];
    const [selectedSlice, setSelectedSlice] = useState<number | null>(null);
    const [hoveredSlice, setHoveredSlice] = useState<number | null>(null);
    const [clickedSlice, setClickedSlice] = useState<{index: number; name: string; value: number; percentage: number; totalRevenue: number} | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const [originalMerchants, setOriginalMerchants] = useState<any[]>([]);
    const [suggestions, setSuggestions] = useState<{ label: string; type: string; id?: string }[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [mapFullscreen, setMapFullscreen] = useState(false);

    // Health score summary computed from current filtered items
    const healthStats = React.useMemo(() => {
        const items = appData?.data?.reportProductItems ?? [];
        const foodItems = items.filter((i: any) => i.health_score != null && i.health_score > 0);
        if (foodItems.length === 0) return null;
        const avg = foodItems.reduce((sum: number, i: any) => sum + i.health_score, 0) / foodItems.length;
        const rounded = Math.round(avg * 10) / 10;
        const grade = rounded >= 7.5 ? 'A' : rounded >= 6 ? 'B' : rounded >= 4.5 ? 'C' : rounded >= 3 ? 'D' : 'F';
        const scored = items.filter((i: any) => i.health_score != null && i.health_score > 0);
        const top3 = [...scored].sort((a: any, b: any) => b.health_score - a.health_score).slice(0, 3);
        const bottom3 = [...scored].sort((a: any, b: any) => a.health_score - b.health_score).slice(0, 3);
        return { avg: rounded, grade, scoredCount: foodItems.length, top3, bottom3 };
    }, [appData?.data?.reportProductItems]);

    const navigate = useNavigate();
    const _api = new SConsciousApiService();
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    const fetchAISuggestions = async (query: string) => {
        if (!query.trim() || query.length < 2) { setSuggestions([]); return; }
        try {
            const [itemSuggs, merchantSuggs] = await Promise.all([
                _api.suggestAI(query, 'product-items', 'sg-items'),
                _api.suggestAI(query, 'merchants', 'sg-merchants'),
            ]);
            const seen = new Set<string>();
            const results: { label: string; type: string; id?: string }[] = [];
            itemSuggs.forEach((s: any) => {
                const text: string = s.text;
                if (!text || seen.has(text)) return;
                seen.add(text);
                if (s.owning_corporation === text) results.push({ label: text, type: 'Corporation' });
                else if (s.brand === text) results.push({ label: text, type: 'Brand' });
                else if (s.product_type === text) results.push({ label: text, type: 'Category' });
                else results.push({ label: text, type: 'Product', id: s.item_id });
            });
            merchantSuggs.forEach((s: any) => {
                const text: string = s.text;
                if (!text || seen.has(text)) return;
                seen.add(text);
                results.push({ label: text, type: 'Merchant', id: s.merchant_id });
            });
            setSuggestions(results.slice(0, 8) as any);
        } catch {
            setSuggestions([]);
        }
    };

    // Generate month options from actual receipt data
    const getMonthOptions = (): MonthOption[] => {
        const dataToUse = originalReceipts.length > 0 ? originalReceipts : appData.data.reportReceipts;
        const uniqueMonths = new Set<string>();
        
        dataToUse.forEach(receipt => {
            if (receipt.transaction_date) {
                const yearMonth = receipt.transaction_date.substring(0, 7); // YYYY-MM
                uniqueMonths.add(yearMonth);
            }
        });

        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                           'July', 'August', 'September', 'October', 'November', 'December'];

        return Array.from(uniqueMonths)
            .sort()
            .reverse() // Most recent first
            .map((yearMonth, idx) => {
                const [year, month] = yearMonth.split('-');
                const monthName = monthNames[parseInt(month) - 1];
                return {
                    id: idx,
                    key: yearMonth,
                    text: `${monthName} ${year}`
                };
            });
    };

    const monthOptions = getMonthOptions();

    useEffect(() => {
        setAppData({...appData, loading: true});
    }, []);

    const applyFilters = async (monthsOverride?: string[], queryOverride?: string, closePanel = true) => {
        const months = monthsOverride ?? selectedMonths;
        const queryRaw = (queryOverride ?? appliedQuery).trim();
        const query = queryRaw.toLowerCase();

        // Store original data if not already stored
        if (originalReceipts.length === 0) {
            setOriginalReceipts([...appData.data.reportReceipts]);
            setOriginalProductItems([...appData.data.reportProductItems]);
            setOriginalBrands([...appData.data.brands]);
            setOriginalCorporations([...appData.data.corporations]);
            setOriginalMerchants([...appData.data.reportMerchants]);
        }

        const dataToFilterReceipts = originalReceipts.length > 0 ? originalReceipts : appData.data.reportReceipts;
        const dataToFilterProductItems = originalProductItems.length > 0 ? originalProductItems : appData.data.reportProductItems;
        const dataToFilterBrands = originalBrands.length > 0 ? originalBrands : appData.data.brands;
        const dataToFilterCorporations = originalCorporations.length > 0 ? originalCorporations : appData.data.corporations;
        const dataToFilterMerchants = originalMerchants.length > 0 ? originalMerchants : appData.data.reportMerchants;

        let filteredReceipts = [...dataToFilterReceipts];
        let filteredProductItems = [...dataToFilterProductItems];

        // Filter by selected months
        if (months.length > 0) {
            filteredReceipts = filteredReceipts.filter(receipt => {
                if (receipt.transaction_date) {
                    const yearMonth = receipt.transaction_date.substring(0, 7); // YYYY-MM
                    return months.includes(yearMonth);
                }
                return false;
            });

            filteredProductItems = filteredProductItems.filter(item => {
                if (item.receipt_transaction_date) {
                    const yearMonth = item.receipt_transaction_date.substring(0, 7); // YYYY-MM
                    return months.includes(yearMonth);
                }
                return false;
            });
        }

        // Filter by search query — Azure AI Search for relevance, local string matching as fallback
        if (queryRaw) {
            const userId = localStorage.getItem('signed_in_id') || '';
            let usedAI = false;
            try {
                const [itemResults, receiptResults] = await Promise.all([
                    _api.searchAI(queryRaw, userId, 'product-items', 500),
                    _api.searchAI(queryRaw, userId, 'receipts', 200),
                ]);
                const aiReceiptIds = new Set<string>([
                    ...itemResults.results.map((r: any) => r.receipt_id).filter(Boolean),
                    ...receiptResults.results.map((r: any) => r.receipt_id).filter(Boolean),
                ]);
                if (aiReceiptIds.size > 0) {
                    filteredReceipts = filteredReceipts.filter(r => aiReceiptIds.has(r.receipt_id));
                    filteredProductItems = filteredProductItems.filter(item => aiReceiptIds.has(item.receipt_id));
                    usedAI = true;
                }
            } catch { /* fall through to local matching */ }

            if (!usedAI) {
                // Local fallback: simple string matching
                const matchByMerchant = new Set(
                    filteredReceipts.filter(r => r.merchant_name?.toLowerCase().includes(query)).map(r => r.receipt_id)
                );
                const corpBrands = new Set(
                    dataToFilterBrands
                        .filter(b => b.owning_corporation?.toLowerCase().includes(query) || b.name?.toLowerCase().includes(query))
                        .map(b => b.name)
                );
                const matchByProduct = new Set(
                    filteredProductItems
                        .filter(item =>
                            item.description?.toLowerCase().includes(query) ||
                            item.brand?.toLowerCase().includes(query) ||
                            item.product_type?.toLowerCase().includes(query) ||
                            item.merchant_name?.toLowerCase().includes(query) ||
                            corpBrands.has(item.brand)
                        )
                        .map(item => item.receipt_id)
                );
                const allIds = new Set([...matchByMerchant, ...matchByProduct]);
                filteredReceipts = filteredReceipts.filter(r => allIds.has(r.receipt_id));
                filteredProductItems = filteredProductItems.filter(item => allIds.has(item.receipt_id));
            }
        }

        // Filter brands - only include brands that appear in filtered receipts/items
        const filteredBrandNames = new Set(filteredProductItems.map(item => item.brand).filter(Boolean));
        const filteredBrands = dataToFilterBrands.filter(brand => filteredBrandNames.has(brand.name));

        // Filter corporations - only include corporations that own filtered brands
        const filteredCorporationNames = new Set(
            filteredBrands.map(brand => brand.owning_corporation).filter(Boolean)
        );
        const filteredCorporations = dataToFilterCorporations.filter(corp =>
            filteredCorporationNames.has(corp.name)
        );

        // Filter merchants - only include merchants whose receipts survived the filter
        const filteredMerchantIds = new Set(filteredReceipts.map(r => (r as any).merchant_id).filter(Boolean));
        const filteredMerchants = dataToFilterMerchants.filter(m => filteredMerchantIds.has(m.merchant_id));

        setAppData({
            ...appData,
            data: {
                ...appData.data,
                reportReceipts: filteredReceipts,
                reportProductItems: filteredProductItems,
                brands: filteredBrands,
                corporations: filteredCorporations,
                reportMerchants: filteredMerchants,
            }
        });

        if (closePanel) setIsPanelOpen(false);
    };

    const clearFilters = () => {
        if (originalReceipts.length > 0) {
            setAppData({
                ...appData,
                data: {
                    ...appData.data,
                    reportReceipts: [...originalReceipts],
                    reportProductItems: [...originalProductItems],
                    brands: [...originalBrands],
                    corporations: [...originalCorporations],
                    reportMerchants: [...originalMerchants],
                }
            });
        }
        setSelectedMonths([]);
        setSearchQuery('');
        setAppliedQuery('');
        setIsPanelOpen(false);
    };

    return ( 

        <Stack style={{ height: '100%', margin: '0 auto', width: '100%', maxWidth: '1400px', display: 'flex', flexDirection: 'column' }}>

            {!appData.dataFetched ? (
                <ProgressBar label="Loading reports" progress={20} />
            ) : (
                <div className="dashboard-layout">
                    {/* ── OLD FILTER SIDEBAR — commented out, search "OLD FILTER" to restore ──
                    <div className="dashboard-item-filter custom-grid-table-container">
                        <SummaryTable
                            data={monthOptions}
                            columns={[
                                {
                                    key: "text",
                                    label: "Filter by Month",
                                    width: "100%",
                                    total: false,
                                    render: (_: any, item: MonthOption) => (
                                        <span 
                                            className='table-cell-content'
                                            onClick={() => {
                                                const newMonths = selectedMonths.includes(item.key)
                                                    ? selectedMonths.filter(key => key !== item.key)
                                                    : [...selectedMonths, item.key];
                                                setSelectedMonths(newMonths);
                                                applyFilters(newMonths, appliedQuery, false);
                                            }}
                                            style={{ 
                                                cursor: 'pointer',
                                                fontWeight: selectedMonths.includes(item.key) ? 600 : 400
                                            }}
                                        >
                                            {selectedMonths.includes(item.key) ? '   ' : '   '}{item.text}
                                        </span>
                                    ),
                                },
                            ]}
                            filterKeys={[]}
                            header={true}
                            total={false}
                            rowsPerPage={5}
                        />
                    </div>
                    ── END OLD FILTER ── */}
                    <div className="dashboard-item-charts"><SpendingCharts onFilter={(months) => applyFilters(months, appliedQuery, false)} onClear={clearFilters} /></div>
                    {healthStats && (
                        <div className="dashboard-item-health">
                            <div className="chart-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <span style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Diet Health Score</span>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '2rem', fontWeight: 800 }}>{healthStats.avg}</span>
                                    <span style={{ fontSize: '0.85rem', color: 'rgba(0,0,0,0.5)' }}>/10 &nbsp;·&nbsp; Grade <strong>{healthStats.grade}</strong> &nbsp;·&nbsp; {healthStats.scoredCount} items scored</span>
                                </div>
                                {/* Score bar */}
                                <div style={{ background: 'rgba(0,0,0,0.10)', borderRadius: 8, height: 8, width: '100%' }}>
                                    <div style={{ background: '#000', borderRadius: 8, height: 8, width: `${(healthStats.avg / 10) * 100}%`, transition: 'width 0.4s' }} />
                                </div>
                                <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                                    <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                                        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, color: 'rgba(0,0,0,0.5)' }}>Healthiest</div>
                                        {healthStats.top3.map((i: any, idx: number) => (
                                            <div key={idx} style={{ fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, padding: '3px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{i.product_identifier || i.description}</span>
                                                <span style={{ background: '#000', color: '#ffcc00', borderRadius: 10, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700, flexShrink: 0 }}>{i.health_score}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                                        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, color: 'rgba(0,0,0,0.5)' }}>Least Healthy</div>
                                        {healthStats.bottom3.map((i: any, idx: number) => (
                                            <div key={idx} style={{ fontSize: '0.78rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, padding: '3px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{i.product_identifier || i.description}</span>
                                                <span style={{ background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.7)', borderRadius: 10, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700, flexShrink: 0 }}>{i.health_score}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="dashboard-item-map" onClick={() => setMapFullscreen(true)} style={{ cursor: 'pointer' }}><HeathMap /></div>
                    {mapFullscreen && <HeathMap fullscreen onClose={() => setMapFullscreen(false)} />}
                    {/* <div className="dashboard-item-receipts"><Receipts /></div>
                    <div className="dashboard-item-products"><ProductItems /></div>
                    <div className="dashboard-item-brands"><Brands /></div>
                    <div className="dashboard-item-corps"><Corporations /></div>
                    <div className="dashboard-item-cats"><Categories /></div>
                    <div className="dashboard-item-merchants"><Merchants /></div> */}
                </div>
            )}
        </Stack>
    )

}